import { API_BASE_URL, ApiError } from './client';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';

/**
 * One car an adult owns. **Global** — it follows its owner between units, exactly as a Scout's own
 * packing list does, so there is no unit in any of these paths.
 */
export interface Vehicle {
  id: string;
  description: string;
  colour: string | null;
  /**
   * Every belted seat in the car, **the driver's own included** (TROOP-136). Every number on the
   * seat ledger is only as honest as what the owner understood this to mean, which is why the help
   * text comes from the server rather than being written twice.
   */
  seatingCapacity: number;
  licensePlate: string | null;
  retiredAt: string | null;
}

export interface MyVehicles {
  vehicles: Vehicle[];
  capacityHelp: string;
  /** Two — the driver takes one seat, so a one-seat car carries nobody. */
  minimumSeatingCapacity: number;
  maximumSeatingCapacity: number;
}

/**
 * What a driver has confirmed about themselves. **A claim, never a check** — the product holds the
 * statement, who made it and when, and says so in those words wherever it renders.
 */
export interface Attestation {
  attested: boolean;
  wording: string;
  wordingVersion: number;
  attestedAt: string | null;
  expiresAt: string | null;
}

/** Outbound | Return | Excursion. Two of the three are singular; a trip during the event repeats. */
export type LegKind = 'Outbound' | 'Return' | 'Excursion';

export interface TransportLeg {
  id: string;
  kind: LegKind;
  label: string;
  /** Instants. Never rendered without the event's zone — see lib/datetime. */
  departsAt: string | null;
  returnsAt: string | null;
  originText: string | null;
  destinationText: string | null;
  sortOrder: number;
}

export interface TransportOffer {
  id: string;
  legId: string;
  vehicleId: string;
  vehicleDescription: string;
  colour: string | null;
  licensePlate: string | null;
  vehicleSeatingCapacity: number;
  /** Seats offered on this leg, **the driver's own included**. */
  seatsOffered: number;
  note: string | null;
  driverPersonId: string;
  driverName: string;
  ownerName: string;
  /** True on a two-seat offer. Derived on render, so there is no flag to go stale. */
  carriesTwoPeople: boolean;
  /**
   * The sentence, or null. States the situation and never a verdict — no red, no compliance
   * language, and no tick implying the opposite case was checked, because for every other offer it
   * was not.
   */
  twoPeopleAdvisory: string | null;
}

/**
 * One leg's arithmetic. `unanswered` renders **beside** the shortfall and is never folded into it:
 * "short by 6, and 4 families have not replied" is a different situation from "short by 6", and the
 * next action differs in each. It arrives as 0 for anybody who is not an adult officer, because who
 * has not replied is a roster disclosure.
 */
export interface SeatLedgerLine {
  legId: string;
  kind: LegKind;
  label: string;
  departsAt: string | null;
  returnsAt: string | null;
  /** People who need to be in a car — **drivers and their children included** (TROOP-136). */
  riders: number;
  maybes: number;
  /** Seats on live offers, **the drivers' own included**. Both halves count people in cars. */
  seatsOffered: number;
  drivers: number;
  /** Declared only. A driver is a rider in a seat their own car supplies, not a self-transporter. */
  selfTransporting: number;
  unanswered: number;
  /** Positive is spare, negative is short. */
  surplus: number;
  surplusWithMaybes: number;
  isCovered: boolean;
}

/**
 * A plan that is an ordinary there-and-back. **Derived by the server on every read, never stored** —
 * the answer stops being true the moment somebody adds a Saturday shuttle, and a flag would go on
 * saying "round trip" with three legs underneath it.
 *
 * **No seat number lives here, and that is deliberate.** A round trip is one card over *two* ledger
 * lines: the way there and the way home are counted separately, printed side by side and never
 * added, because a Friday surplus cancelling a Sunday shortfall is TROOP-135 exactly.
 */
export interface RoundTrip {
  outboundLegId: string;
  returnLegId: string;
  from: string | null;
  to: string | null;
}

export interface TransportPlan {
  legs: TransportLeg[];
  ledger: SeatLedgerLine[];
  offers: TransportOffer[];
  /** Null whenever the plan is genuinely several trips. */
  roundTrip: RoundTrip | null;
}

/** Blocked | EligibleAttestedOnly | EligibleDocumented | Expiring. Never a boolean. */
export type DriverStandingState =
  | 'Blocked'
  | 'EligibleAttestedOnly'
  | 'EligibleDocumented'
  | 'Expiring';

export interface Driver {
  personId: string;
  displayName: string;
  standing: DriverStandingState;
  /** What to do about it, or null. Always present when blocked. */
  missing: string | null;
  lapsesAt: string | null;
  vehicles: Vehicle[];
}

export type SeatNeed = 'NeedsSeat' | 'SelfTransport';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (response.status === 400 || response.status === 409) {
    // Both shapes arrive here: a ValidationProblem carries `errors`, a refusal carries `detail`.
    // Surfacing the sentence matters more than usual on these forms — "your vehicle is recorded as
    // seating 6, to offer more update the vehicle" is the difference between a fix and a retry.
    const problem = (await response.json().catch(() => null)) as
      | { errors?: FieldErrors; detail?: string; title?: string }
      | null;

    if (problem?.errors) throw new ValidationFailure(problem.errors);
    throw new ApiError(response.status, problem?.detail ?? problem?.title ?? 'That could not be saved.');
  }

  if (!response.ok) {
    throw new ApiError(response.status, `${init?.method ?? 'GET'} ${path} failed`);
  }

  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

/**
 * **Two doors, one set of rules.** A member works under `/units/`; a guardian holds no membership
 * and would be 404'd there, so their path is `/family`. The shapes are identical on purpose — the
 * screens are the same components, and a second set of types here would be the first step towards a
 * second set of rules.
 */
const base = (unitId: string | null, eventId: string) =>
  unitId ? `/units/${unitId}/events/${eventId}/transport` : `/family/events/${eventId}/transport`;

export const transportApi = {
  myVehicles: () => call<MyVehicles>('/me/vehicles'),

  addVehicle: (body: Omit<Vehicle, 'id' | 'retiredAt'>) =>
    call<Vehicle>('/me/vehicles', { method: 'POST', body: JSON.stringify(body) }),

  updateVehicle: (vehicleId: string, body: Omit<Vehicle, 'id' | 'retiredAt'>) =>
    call<{ vehicle: Vehicle; offersLowered: number }>(`/me/vehicles/${vehicleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  retireVehicle: (vehicleId: string) =>
    call<unknown>(`/me/vehicles/${vehicleId}/retire`, { method: 'POST' }),

  myAttestation: () => call<Attestation>('/me/driver'),

  attest: (wordingVersion: number) =>
    call<Attestation>('/me/driver/attest', {
      method: 'POST',
      body: JSON.stringify({ wordingVersion }),
    }),

  plan: (unitId: string | null, eventId: string) => call<TransportPlan>(`${base(unitId, eventId)}/`),

  drivers: (unitId: string, eventId: string) =>
    call<Driver[]>(`/units/${unitId}/events/${eventId}/transport/drivers`),

  addLeg: (
    unitId: string,
    eventId: string,
    body: {
      kind: LegKind;
      label?: string | null;
      /** A local reading like `2026-10-30T17:00`. No offset, no trailing Z. */
      departsAt?: string | null;
      returnsAt?: string | null;
      originText?: string | null;
      destinationText?: string | null;
    },
  ) =>
    call<TransportLeg>(`/units/${unitId}/events/${eventId}/transport/legs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Edits a leg that already exists — the places, the label and the times. **Not the kind**: an
   * Outbound turned into a Return is a different leg with different offers standing against it, and
   * §15's partial unique index would refuse the move anyway, so the form does not offer it.
   *
   * The endpoint has existed since TROOP-50 and nothing called it, which left *Remove this trip* as
   * the only way to fix a typo in a destination — withdrawing every offer made against the leg.
   */
  updateLeg: (
    unitId: string,
    eventId: string,
    legId: string,
    body: {
      kind: LegKind;
      label?: string | null;
      departsAt?: string | null;
      returnsAt?: string | null;
      originText?: string | null;
      destinationText?: string | null;
    },
  ) =>
    call<TransportLeg>(`/units/${unitId}/events/${eventId}/transport/legs/${legId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /**
   * Mints the Outbound and the Return of one there-and-back, mirrored, in a single call.
   *
   * **One call rather than two, and that is the rule rather than a saving.** Two posts are two
   * chances for the mirroring to half-apply, and half a round trip is exactly the state this
   * replaced: an event with a way there, no way home, and a screen that renders it as finished.
   */
  addRoundTrip: (
    unitId: string,
    eventId: string,
    body: {
      from?: string | null;
      to?: string | null;
      /** A local reading like `2026-10-30T17:00`. No offset, no trailing Z. */
      leavesAt?: string | null;
      /** When the family is home again — the return leg's `returnsAt`. */
      getsBackAt?: string | null;
    },
  ) =>
    call<RoundTrip>(`/units/${unitId}/events/${eventId}/transport/round-trip`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** The same fact edited: the `to` moves the outbound's destination and the return's origin together. */
  updateRoundTrip: (
    unitId: string,
    eventId: string,
    body: {
      from?: string | null;
      to?: string | null;
      leavesAt?: string | null;
      getsBackAt?: string | null;
    },
  ) =>
    call<RoundTrip>(`/units/${unitId}/events/${eventId}/transport/round-trip`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  removeLeg: (unitId: string, eventId: string, legId: string) =>
    call<{ offersWithdrawn: number }>(
      `/units/${unitId}/events/${eventId}/transport/legs/${legId}`,
      { method: 'DELETE' },
    ),

  offer: (
    unitId: string | null,
    eventId: string,
    legId: string,
    body: { vehicleId: string; seatsOffered?: number | null; note?: string | null },
  ) =>
    call<{ offerId: string; seatsOffered: number; carriesTwoPeople: boolean; advisory: string | null }>(
      `${base(unitId, eventId)}/legs/${legId}/offers`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  withdraw: (unitId: string | null, eventId: string, offerId: string) =>
    call<unknown>(`${base(unitId, eventId)}/offers/${offerId}`, { method: 'DELETE' }),

  setSeatNeed: (
    unitId: string | null,
    eventId: string,
    legId: string,
    personId: string,
    need: SeatNeed,
    drivenByName?: string | null,
  ) =>
    call<unknown>(`${base(unitId, eventId)}/legs/${legId}/seats/${personId}`, {
      method: 'PUT',
      body: JSON.stringify({ need, drivenByName: drivenByName ?? null }),
    }),
};
